import type { AdminPeriod } from '../period/period-types';

/**
 * The Customers page's read models, as the contract shapes them (paige-v1.yaml: CustomerRow, CustomerList,
 * CustomerSummary, PartnerHits) and what the page asks with. The ledger searches, filters, sorts and adds
 * up; the browser never holds more than one page of companies.
 */

/** The saved views, as the ledger rules them (CustomerViewName). */
export const customerViews = [
  'all',
  'call-today',
  'running-out',
  'expansion',
  'slipping',
  'needs-us',
  'overdue',
  'not-activated',
] as const;
export type CustomerViewName = (typeof customerViews)[number];

/** The orders the ledger sorts the list by. */
export const customerSorts = ['name', 'signedUp', 'pages', 'paid', 'lastActive'] as const;
export type CustomerSort = (typeof customerSorts)[number];
export type SortDir = 'asc' | 'desc';

/** What the list and its numbers are narrowed to. A switch that is off is not sent. */
export interface CustomerFilterSet {
  q: string;
  /** A package key; `free` for companies with none. */
  plan: string | null;
  paying: boolean;
  pastDue: boolean;
  test: boolean;
  problems: boolean;
  view: CustomerViewName;
  /** A partner's four-character code, or `none`. */
  partner: string | null;
}

export const noFilters: CustomerFilterSet = {
  q: '',
  plan: null,
  paying: false,
  pastDue: false,
  test: false,
  problems: false,
  view: 'all',
  partner: null,
};

export interface CustomerRow {
  id: string;
  name: string;
  testCompany: boolean;
  plan: string | null;
  partner: string | null;
  signedUpAt: string;
  lastActiveAt: string | null;
  pages: number;
  previousPages: number;
  paid: number;
  previousPaid: number;
  paidAllTime: number;
  pastDue: boolean;
  problems: number;
  creditsHeld: boolean;
  runwayDays: number | null;
  last30: number;
  prior30: number;
  firstPagesAt: string | null;
  lastPagesAt: string | null;
  owner: string | null;
  nextStep: { body: string; dueOn: string | null } | null;
}

export interface CustomerList {
  total: number;
  offset: number;
  limit: number;
  period: AdminPeriod;
  rows: CustomerRow[];
}

export interface CustomerMover {
  id: string;
  name: string;
  testCompany: boolean;
  /** Pages read in the last 30 days. */
  pages: number;
  /** The 30 days before. */
  previousPages: number;
}

export type MoverBand = 'down50' | 'down15' | 'steady' | 'up15' | 'up50' | 'new' | 'none';

export interface CustomerMovers {
  growing: CustomerMover[];
  slowing: CustomerMover[];
  bands: { band: MoverBand; companies: number }[];
}

/** One mark of the sales picture: a day, a week or a month of the period. */
export interface SalesPoint {
  from: string;
  until: string;
  revenue: number;
  newPaying: number;
  pages: number;
  /** The same bucket of the period before. */
  previousRevenue: number;
  previousNewPaying: number;
  previousPages: number;
}

export interface CustomerSummary {
  asOf: string;
  period: AdminPeriod;
  companies: number;
  tests: number;
  pages: number;
  previousPages: number;
  revenue: number;
  previousRevenue: number;
  paying: number;
  previousPaying: number;
  newPaying: number;
  previousNewPaying: number;
  pastDue: number;
  withProblems: number;
  views: Record<string, number>;
  plans: { plan: string; companies: number }[];
  bucket: 'day' | 'week' | 'month';
  series: SalesPoint[];
  movers: CustomerMovers;
}

/** A partner as the picker lists it: its code and how many companies are linked to it now. */
export interface PartnerHit {
  code: string;
  companies: number;
}

/** The series the sales picture draws large. */
export const salesMetrics = ['revenue', 'newPaying', 'pages'] as const;
export type SalesMetric = (typeof salesMetrics)[number];
